import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const PAGE_SIZE = 50;
const CACHE_TTL_MS = 30_000; // re-use server data for 30 s between navigations

// Simple module-level cache shared across all hook instances
const _cache = new Map();

/** Invalida caché tras crear/editar producto (p. ej. cambio is_dispensario). */
export function clearAllProductsCache() {
  _cache.clear();
}

/**
 * @param {string} token
 * @param {string} venueID
 * @param {{ dispensarioOnly?: boolean, excludeDispensario?: boolean, categoryName?: string|null, searchText?: string, forceWide?: boolean, includeInactive?: boolean }} [options]
 *   includeInactive: solo la pantalla de GESTIÓN de productos, para poder reactivar
 *   una referencia retirada. Las pantallas de venta jamás lo piden.
 *   searchText: término de búsqueda. Viaja como `search` al servidor (que lo aplica a
 *   nombre, categoría e id cuando la API lo soporta; una API vieja lo ignora sin
 *   romper) y activa la carga ancha para que el filtro en cliente tenga base.
 *   forceWide: carga ancha (500) sin filtro de servidor — lo pide la píldora
 *   «Sin categoría», que se resuelve en cliente y con 50 filas vería media lista.
 */
export default function useProducts(token, venueID, options = {}) {
  const { dispensarioOnly = false, excludeDispensario = false, categoryName = null, searchText = '', forceWide = false, includeInactive = false } = options;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Abort controller so stale requests don't stomp fresh ones
  const abortRef = useRef(null);

  // Una sola forma de construir la clave: refreshProducts la reutiliza. (Antes cada
  // llamada pasaba los argumentos a mano y a deleteProduct le faltaba includeInactive:
  // invalidaba una clave que no existía y el producto borrado seguía viéndose hasta
  // caducar el TTL.) TODOS los segmentos se codifican: venue, categoría y término los
  // escribe el usuario y un «|» dentro de cualquiera colisionaría claves.
  const keyFor = useCallback(
    (pageIndex) =>
      [venueID, dispensarioOnly, excludeDispensario, pageIndex, categoryName ?? '', searchText, forceWide, includeInactive ? 'all' : 'active']
        .map((v) => encodeURIComponent(String(v)))
        .join('|'),
    [venueID, dispensarioOnly, excludeDispensario, categoryName, searchText, forceWide, includeInactive]
  );

  // Solo la petición más reciente puede apagar el spinner: la abortada resuelve su
  // finally DESPUÉS de que la sustituta hiciera setLoading(true), y sin esto la
  // pantalla se quedaba con loading=false y la lista vieja mientras aún llegaba la nueva.
  const fetchSeq = useRef(0);

  const fetchProducts = useCallback(async (pageIndex = 0) => {
    if (!token || !venueID) return;

    const wideFetch = Boolean(categoryName || searchText || forceWide);
    const key = keyFor(pageIndex);
    const cached = _cache.get(key);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setProducts(cached.data);
      // When filtering by category or search we load up to 500 items — no "load more" needed
      setHasMore(!wideFetch && cached.data.length === PAGE_SIZE);
      return;
    }

    // Cancel any in-flight request for this hook instance
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const seq = ++fetchSeq.current;
    setLoading(true);
    setErrorMessage('');
    try {
      // Category filter or active search: fetch up to 500 so name/category/id search is reliable.
      const params = {
        skip: wideFetch ? 0 : pageIndex * PAGE_SIZE,
        limit: wideFetch ? 500 : PAGE_SIZE,
        ...(dispensarioOnly ? { dispensario_only: true } : {}),
        ...(excludeDispensario ? { exclude_dispensario: true } : {}),
        ...(categoryName ? { category_name: categoryName } : {}),
        ...(searchText ? { search: searchText } : {}),
        ...(includeInactive ? { include_inactive: true } : {}),
      };
      const { data } = await axios.get('/v1/get-venue-products', {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'x-venue-id': venueID,
        },
        signal: abortRef.current.signal,
      });
      const list = Array.isArray(data) ? data : [];
      _cache.set(key, { data: list, ts: Date.now() });
      // Barrido de caducados: las claves llevan el término de búsqueda, así que sin
      // esto cada término dejaría un listado de hasta 500 productos retenido para
      // siempre en este Map de módulo.
      for (const [k, v] of _cache) {
        if (Date.now() - v.ts >= CACHE_TTL_MS) _cache.delete(k);
      }
      setProducts(list);
      setHasMore(!wideFetch && list.length === PAGE_SIZE);
    } catch (err) {
      if (axios.isCancel(err)) return;
      const detail = err.response?.data?.detail || err.message || 'Error fetching products';
      setErrorMessage(detail);
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [token, venueID, dispensarioOnly, excludeDispensario, categoryName, searchText, forceWide, includeInactive, keyFor]);

  const goToPage = useCallback((newPage) => {
    setPage(newPage);
    fetchProducts(newPage);
  }, [fetchProducts]);

  /** Devuelve true/false para que el borrado masivo pueda contar fallos de verdad
   *  (los errores se tragan aquí a propósito: la pantalla lee errorMessage). */
  /**
   * Borra una referencia. Devuelve `{ok, detail}` — y el `detail` importa: el 409 del
   * backend explica POR QUÉ no se puede (ventas, comandas, movimientos de almacén,
   * carrito abierto) y ofrece la salida buena.
   *
   * Antes devolvía un booleano y el motivo solo vivía en el `errorMessage` del hook.
   * `Products.jsx` limpiaba ese mensaje justo después de llamar, y el efecto que lo
   * recopiaba solo se dispara cuando el STRING cambia — así que al segundo 409
   * idéntico el mensaje desaparecía de pantalla y el borrado parecía no hacer nada.
   */
  const deleteProduct = useCallback(async (id) => {
    if (!token) return { ok: false, detail: 'Falta autenticación' };
    setErrorMessage('');
    try {
      await axios.delete(`/v1/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Se limpia TODO el caché, como en setProductActive: con el término de búsqueda
      // en la clave, invalidar solo la actual dejaría al producto borrado reapareciendo
      // al cambiar de píldora o vaciar el buscador dentro del TTL.
      clearAllProductsCache();
      await fetchProducts(page);
      return { ok: true, detail: null };
    } catch (err) {
      const detail = err.response?.data?.detail || 'No se pudo eliminar el producto';
      setErrorMessage(detail);
      return { ok: false, detail };
    }
  }, [token, page, fetchProducts]);

  const refreshProducts = useCallback(() => {
    // Force bypass cache on manual refresh
    _cache.delete(keyFor(page));
    fetchProducts(page);
  }, [page, keyFor, fetchProducts]);

  /**
   * Retira (o reactiva) una referencia. NO borra: el producto conserva ficha, stock e
   * historial, y al reactivarlo vuelve tal cual — borrar uno con ventas rompería la
   * contabilidad del histórico.
   *
   * Se manda SOLO `is_active` porque la lista blanca del PUT ignora lo ausente: así el
   * interruptor no puede pisar precio, stock ni categoría sin querer.
   */
  const setProductActive = useCallback(async (id, active) => {
    if (!token) return false;
    setErrorMessage('');
    try {
      const body = new FormData();
      body.append('product', JSON.stringify({ is_active: !!active }));
      const res = await fetch(`/v1/products/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || 'No se pudo cambiar el estado del producto');
      }
      // El producto sale (o entra) en las listas de VENTA, que tienen su propia clave de
      // caché: se limpia todo, no solo la de esta pantalla.
      clearAllProductsCache();
      await fetchProducts(page);
      return true;
    } catch (err) {
      setErrorMessage(err.message || 'No se pudo cambiar el estado del producto');
      return false;
    }
  }, [token, page, fetchProducts]);

  useEffect(() => {
    setPage(0);
    fetchProducts(0);
  }, [fetchProducts]);

  return {
    products,
    loading,
    errorMessage,
    page,
    hasMore,
    pageSize: PAGE_SIZE,
    goToPage,
    refreshProducts,
    deleteProduct,
    setProductActive,
  };
}
